import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Avatar, Divider, Drawer } from 'antd'
import { UserContext } from '../../core/user-context/user-context-middleware'
import { useMsal } from '@azure/msal-react'
import { homePath } from '../../routing'
import {
  adminPath,
  peerSupporterLeadPathStub,
  supporterPathStub,
} from '../../routing/route.constants'
import { IProfileItems, Roles, decryptIdToken } from '../../pages/constants'
import {
  HomeOutlined,
  BellOutlined,
  TeamOutlined,
  LogoutOutlined,
  CloseOutlined,
  UserOutlined,
  ProfileOutlined,
} from '@ant-design/icons'
import './sideMenu.scss'
import ProfilePageForm from './profile-page-form'
import { FormDrawer } from '../../common'
import { globalConfig } from '../../configuration/config'
import { API_URLs } from '../../configuration/api-urls'
import { IRecentNotifications } from './app-header'
import { getArticle, getLastUpdatedAt } from '../../common/utils/utils'
import { decryptToken } from '../authentication/request-token/request-token'

export type LogoutFunction = () => void

interface SideMenuProps {
  visible: boolean
  onClose: () => void
  logOut?: LogoutFunction
  userRole: string
  currentNotifications?: IRecentNotifications[]
}

const SideMenu: React.FC<SideMenuProps> = ({
  visible,
  onClose,
  userRole,
  currentNotifications,
}) => {
  const userDetails = useContext(UserContext)
  const { instance } = useMsal()
  const { userDisplayName, userEmail } = userDetails
  const [isMobileNotificationDrawerOpen, setIsMobileNotificationDrawerOpen] =
    useState(false)
  const history = useHistory()

  const getProfileFetchUrl = globalConfig
    .get()
    .apiBaseUrl.concat(API_URLs.v1.GET_Profile)

  const postProfileFetchUrl = globalConfig
    .get()
    .apiBaseUrl.concat(API_URLs.v1.POST_Profile)

  const [profileData, setProfileData] = useState<IProfileItems>()

  const [isProfilePageOpen, setIsProfilePageOpen] = useState(false)

  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState<boolean>(false)

  const [idToken, setIdToken] = useState<string | null>(null)

  const profile= {
    peerContactNumber: "",
    peerEmail: "",
    peerName: ""
  }

  useEffect(() => {
    const fetchDecryptedToken = async () => {
      const decryptedToken = await decryptIdToken();
      setIdToken(decryptedToken);
    };

    fetchDecryptedToken();
  }, []);

  const ToggleIsFormDrawerOpen = () => {
    setIsFormDrawerOpen((prev) => !prev)
  }

  const toggleForm = () => {
    setIsProfilePageOpen(true)
    setIsFormDrawerOpen(true)
  }

  interface ISideMenuNavLinks {
    icon: any
    text: string
    onClick: () => void
    roles: Roles[]
  }

  const SideMenuNavLinks: ISideMenuNavLinks[] = [
    {
      icon: HomeOutlined,
      text: 'Home',
      onClick: () => {
        history.push(homePath)
        onClose()
      },
      roles: [
        Roles.SUPPORTER,
        Roles.PEER_SUPPORTER_LEAD,
        Roles.ADMIN,
        Roles.ADMIN_SUPPORTER,
        Roles.ADMIN_LEAD,
        Roles.LEAD_SUPPORTER,
        Roles.ALL,
      ],
    },
    {
      icon: ProfileOutlined,
      text: 'Profile',
      onClick: () => {
        fetch(getProfileFetchUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            return response.json()
          })
          .then((data) => {
            setProfileData(data? data: profile)
          })
          .catch((error) => {
            console.log(error)
            fetch(postProfileFetchUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${decryptToken(sessionStorage.getItem('idToken'))}`,
              },
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`)
                }
                return response.json()
              })
              .then((data) => {
                setProfileData(data? data: profile)
              })
              .catch((error) => {
                console.log(error)
              })
          })
        toggleForm()
      },
      roles: [
        Roles.SUPPORTER,
        Roles.ADMIN,
        Roles.ADMIN_SUPPORTER,
        Roles.PEER_SUPPORTER_LEAD,
        Roles.ADMIN_LEAD,
        Roles.LEAD_SUPPORTER,
        Roles.ALL,
      ],
    },
    {
      icon: TeamOutlined,
      text: 'Supporter',
      onClick: () => {
        history.push(supporterPathStub.concat('/pending-conversations'))
        onClose()
      },
      roles: [
        Roles.SUPPORTER,
        Roles.ADMIN_SUPPORTER,
        Roles.LEAD_SUPPORTER,
        Roles.ALL,
      ],
    },
    {
      icon: UserOutlined,
      text: 'Peer Supporter Lead',
      onClick: () => {
        history.push(peerSupporterLeadPathStub.concat('/supporter-management'))
        onClose()
      },
      roles: [
        Roles.PEER_SUPPORTER_LEAD,
        Roles.LEAD_SUPPORTER,
        Roles.ADMIN,
        Roles.ADMIN_SUPPORTER,
        Roles.ADMIN_LEAD,
        Roles.ALL,
      ],
    },
    {
      icon: UserOutlined,
      text: 'Admin',
      onClick: () => {
        history.push(adminPath)
        onClose()
      },
      roles: [
        Roles.ADMIN,
        Roles.ADMIN_SUPPORTER,
        Roles.LEAD_SUPPORTER,
        Roles.ALL,
      ],
    },
    {
      icon: BellOutlined,
      text: 'Notifications',
      onClick: () => {
        // TODO: Open notifications
        setIsMobileNotificationDrawerOpen(true)
        // onClose()
      },
      roles: [
        Roles.SUPPORTER,
        Roles.ADMIN,
        Roles.ADMIN_SUPPORTER,
        Roles.PEER_SUPPORTER_LEAD,
        Roles.ADMIN_LEAD,
        Roles.LEAD_SUPPORTER,
        Roles.ALL,
      ],
    },
    {
      icon: LogoutOutlined,
      text: 'Log Out',
      onClick: () => {
        instance.logoutRedirect().catch((e: Error) => {
          console.error(e)
        })
        onClose()
      },
      roles: [
        Roles.USER,
        Roles.SUPPORTER,
        Roles.ADMIN,
        Roles.ADMIN_SUPPORTER,
        Roles.PEER_SUPPORTER_LEAD,
        Roles.ADMIN_LEAD,
        Roles.LEAD_SUPPORTER,
        Roles.ALL,
      ],
    },
  ]

  const userName = userDisplayName?.split(',').reverse().join(' ')

  const updatePhoneNumber = (newPhoneNumber: any) => {
    setProfileData((prevData) => {
      if (!prevData) {
        return {
          peerContactNumber: newPhoneNumber,
          peerEmail: '',
          peerName: '',
        }
      }
      return {
        ...prevData,
        peerContactNumber: newPhoneNumber,
      }
    })
  }

  return (
    <Drawer
      title=""
      placement="right"
      closable={false}
      onClose={onClose}
      open={visible}
      className="block lg:hidden"
    >
      <div className="space-y-4">
        <div className="bg-component-biege">
          <CloseOutlined
            className="cursor-pointer p-[1rem] text-black text-lg float-right mb-4"
            onClick={onClose}
          />
          <div className="flex flex-row gap-1 pt-[1.25rem] pl-[1rem]">
            <UserAvatar />
          </div>
          <div className="flex flex-col pl-[1rem] items-start justify-center">
            <div className="text-xl max-w-xs text-ellipsis font-semibold pt-1">
              {userName}
            </div>
            <div className="text-base pb-[1rem] font-semibold max-w-xs text-ellipsis pt-1">
              {userEmail}
            </div>
          </div>
        </div>
        <div>
          {SideMenuNavLinks.map((navLink, index) => {
            const { icon: Icon, text, onClick, roles } = navLink
            if (roles.some((role) => role === userRole))
              return (
                <div
                  className="space-x-6 cursor-pointer p-[1rem] text-xl hover:bg-component-biege hover:font-bold text-black active:bg-component-biege active:font-bold selected:text-black selected:bg-component-biege selected:font-bold"
                  onClick={onClick}
                  key={index}
                >
                  <Icon className="text-2xl" />
                  <span className="text-base">{text}</span>
                </div>
              )
          })}
        </div>
        {isProfilePageOpen ? (
          <FormDrawer
            title={'Your Profile'}
            child={
              isProfilePageOpen ? (
                <ProfilePageForm
                  onClose={ToggleIsFormDrawerOpen}
                  isVisible={isFormDrawerOpen}
                  data={profileData}
                  updatePhoneNumber={updatePhoneNumber}
                />
              ) : (
                <></>
              )
            }
            isOpen={isFormDrawerOpen}
            onClose={ToggleIsFormDrawerOpen}
          />
        ) : null}

        {isMobileNotificationDrawerOpen ? (
          <Drawer
            title={'Notifications'}
            open={isMobileNotificationDrawerOpen}
            onClose={() => setIsMobileNotificationDrawerOpen(false)}
            className="md:hidden"
          >
            <div className="flex flex-col bg-white h-full">
              {currentNotifications?.map(
                (item: IRecentNotifications, index: number) => {
                  return (
                    <div
                      className="w-full flex flex-col gap-2 bg-white drop-shadow-sm p-2"
                      key={index}
                    >
                      <div className="font-normal text-sm text-black">
                        {getArticle(item.roleType.name)} {item.roleType.name}{' '}
                        from {item.location.name} raised a P2P support request
                      </div>
                      <div className="font-light text-xs">
                        {getLastUpdatedAt(item.createdAt)}
                      </div>
                      {index < currentNotifications.length - 1 && (
                        <Divider style={{ margin: 0 }} />
                      )}
                    </div>
                  )
                }
              )}
            </div>
          </Drawer>
        ) : null}
      </div>
    </Drawer>
  )
}

const UserAvatar: React.FC = () => {
  const { userPhoto, userInitials } = useContext(UserContext)
  return (
    <div>
      {userPhoto ? (
        <Avatar size={64} src={userPhoto} />
      ) : (
        <Avatar size={64} className="bg-white-600">
          {userInitials}
        </Avatar>
      )}
    </div>
  )
}

export default SideMenu
